import React, { FC, useCallback, useMemo, useRef, useState } from "react";
import { t } from "@bookingcom/lingojs-core";
import { I18nChildContext, useI18n } from "@bookingcom/lingojs-react";
import { useLocation } from "react-router-dom";

import FlightCardPrice from "./FlightCardPrice";
import { Button, Card, Icon, Stack, useViewport } from "@bookingcom/bui-react";
import { FlightBadgeVariant, UIFlightData, UIOrder, UITripRebookOption, UIRebookFlow } from "@flights/types";

import BaseFareAttributes from "./BaseFareAttributes";

import useLocaleContext from "../../../../hooks/useLocaleContext";
import { SBSearchType } from "@bookingcom/flights-searchbox";
import { ArrowNavDownIcon, ArrowNavUpIcon } from "@bookingcom/bui-assets-react/streamline/index.js";
import { FareSelectorDesktop } from "../../../../branded-fares/components/FareSelector/components/FareSelector.desktop";
import useIsInViewport from "../../../../hooks/useIsInViewport";
import useFetchBrandedFares from "../../../../branded-fares/hooks/useFetchBrandedFares";
import { RebookCostBreakdown } from "../../RebookFlow/RebookCostBreakdown/RebookCostBreakdown";
import { FlightCardHighlight, FlightCardHighlightStyle } from "./FlightCardHighlight";
import { FlightCardBadges } from "./FlightCardBadges";
import { FlightCardBadge } from "./FlightCardBadge";
import Frame, { FrameProps } from "../../Frame";
import { getCampaignBadgesConfig, getCampaignBestOfferHighlightConfig } from "../../../../utils/flightCampaignConfig";
import styles from "./FlightCardBound.desktop.module.css";
import flights_apex_web_ancillaries_micro_conversion_aa from "../../../../utils/experiments/apex/flights_apex_web_ancillaries_micro_conversion_aa";
import { isOfMetaOrigin } from "../../../../utils/marketing-url-params";
import { carrierUtils, flightsSessionStore } from "@flights/web-api-utils-universal";
import useGlobalContext from "../../../../hooks/useGlobalContext";
import { useSelector } from "react-redux";
import { getSearchCriteria } from "../../../../store/searchCriteria/selectors";
import { getSearchDisplayOptions } from "../../../../store/searchResults/selectors";
import { FlightCardLuggageDesktop } from "./elements/FlightCardLuggage/FlightCardLuggage.desktop";
import { CardSegmentsCondensed } from "./elements/CardSegmentCondensed/CardSegmentsCondensed";
import { mcn } from "../../../../utils/mergeClassnames";
import FlightCardPriceOutbound from "./FlightCardPrice.outbound";
import CardSplitBoundsLuggageAndBadgesRendererDesktop from "./elements/CardSplitBoundsLuggageAndBadgesRenderer.desktop";
import flights_apex_web_cfar_on_sr_fd_v2 from "../../../../utils/experiments/apex/flights_apex_web_cfar_on_sr_fd_v2";
import { useSplitBoundsCondition } from "../../../../utils/experiments/funnel/flights_split_bounds_us";
import { trackWithDefaultStage } from "@flights/et-universal";
import usePointOfSale from "../../../../hooks/usePointOfSale";
import useRouteName from "../../../../hooks/useRouteName";
import flights_web_flight_card_update_desktop from "../../../../utils/experiments/funnel/flights_web_flight_card_update_desktop";
import flights_merch_toolkit_offer_pinning_v2 from "../../../../utils/experiments/genius/flights_merch_toolkit_offer_pinning_v2";

export type FlightCardBoundProps = {
  flight: UIFlightData;
  onClick: (flight: UIFlightData) => void;
  pricePerPerson?: boolean;
  index: number;
  bigLogo?: boolean;
  searchType?: SBSearchType;
  rebookProps?: {
    tripRebookOption: UITripRebookOption;
    initialOrder: UIOrder;
    align: "start" | "end";
  };
  mt?: FrameProps["mt"];
  mb?: FrameProps["mb"];
  isAlternativeFareCard?: boolean;
  forceHideLastAvailableSeats?: boolean;
  /* start - flights_split_bounds_us */
  boundsVariant?: "outbound" | "inbound";
  /* end - flights_split_bounds_us */
  /* start - flights_alternative_fares_for_price_change_and_sold_out_errors_web */
  viewFlightDetailsCtaLabel?: string;
  disabled?: boolean;
  /* end - flights_alternative_fares_for_price_change_and_sold_out_errors_web */
};

const FlightCardBound: FC<FlightCardBoundProps> = (props) => {
  const { flight, onClick, pricePerPerson, index, bigLogo, rebookProps, boundsVariant } = props;
  const i18n = useI18n() as I18nChildContext;
  const { isRTL } = useLocaleContext();
  const { isInternal } = useGlobalContext();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const rebookFlow = queryParams.get("rebookFlow") as UIRebookFlow | null;
  const flightCardBoundRef = useRef<HTMLDivElement>(null);
  const isInViewport = useIsInViewport(flightCardBoundRef.current);
  const { adults, children } = useSelector(getSearchCriteria);
  const displayOptions = useSelector(getSearchDisplayOptions);
  const campaignBadges = getCampaignBadgesConfig(flight.campaignDisplay?.badges);
  const carriers = carrierUtils.getMarketingCarriers(flight.segments);
  const { isLarge } = useViewport();
  const pointOfSale = usePointOfSale();
  const routeName = useRouteName();

  const hasFlexibleTicketBadge = boundsVariant !== "outbound" && flight.ancillaries?.flexibleTicket;

  const canShowCfarBadge =
    boundsVariant !== "outbound" &&
    flight.ancillaries?.cancelForAnyReason &&
    flights_apex_web_cfar_on_sr_fd_v2.isEligablePos(pointOfSale);

  const showCfarBadge = canShowCfarBadge && !!flights_apex_web_cfar_on_sr_fd_v2.trackWithDefaultStage();
  const cfarBadgeCopyTag =
    hasFlexibleTicketBadge && canShowCfarBadge ? t("flights_sr_flexibility_badge") : t("flights_sr_cfar_only_badge");

  const cfarBadgeRef = useRef<HTMLSpanElement>(null);

  if (canShowCfarBadge && routeName.startsWith("searchresults")) {
    // search results within first two cards without scrolling
    const hasNotScrolled = typeof window !== "undefined" && window.scrollY === 0;
    const shouldTrackSrLess2 = index <= 1 && isInViewport && hasNotScrolled;

    const badgeHeight = cfarBadgeRef.current?.offsetHeight ?? 0;
    const isMultiline = badgeHeight > 22;

    flights_apex_web_cfar_on_sr_fd_v2.trackSearchResultsStages(
      false,
      isMultiline,
      !hasFlexibleTicketBadge && canShowCfarBadge,
      shouldTrackSrLess2
    );
  }

  const { isSplitBounds } = useSplitBoundsCondition();

  const showFlightCardBadges =
    flight?.badges?.length ||
    (!isSplitBounds && hasFlexibleTicketBadge) ||
    flight.hasAppleCardBenefits ||
    showCfarBadge;

  const { bestOfferHighlightStyle, bestOfferHighlightText } = getCampaignBestOfferHighlightConfig(
    flight.campaignDisplay?.bestOfferHighlight
  );
  const shouldShowBestOfferHightlight = !!(bestOfferHighlightStyle && bestOfferHighlightText);

  const shouldPinMetaOfferOnSrVariant =
    !!trackWithDefaultStage("flights_web_cat_pinning_meta_offer_on_sr_www", 1) &&
    isOfMetaOrigin() &&
    flightsSessionStore.get("landingOfferKey") === flight.offerKeyToHighlight;

  const shouldPinOfferOnSr =
    (flight.highlightInfo?.highlight &&
      (flight.highlightInfo.reason === "CROSS_SELL_OFFER" ||
        flight.highlightInfo.reason === "GENIUS_AIRLINE_FARE_OFFER")) ||
    shouldPinMetaOfferOnSrVariant;

  /* start - flights_merch_toolkit_offer_pinning_v2 */
  const isFeaturedOffer =
    flight.highlightInfo?.highlight && flight.highlightInfo.reason === "GENIUS_AIRLINE_FARE_OFFER";

  const pinnedHighlightText = shouldPinMetaOfferOnSrVariant
    ? i18n.trans(t("flights_sr_pin_meta_banner_selected"))
    : isFeaturedOffer
    ? flight.highlightInfo?.message ?? ""
    : flight.highlightInfo?.message || i18n.trans(t("flights_meta_sr_pinned_flight_label"));

  const pinnedInfoTooltip =
    isFeaturedOffer && flight.highlightInfo?.infoMessage
      ? {
          content: flight.highlightInfo.infoMessage.subtitle,
          ariaLabel: i18n.trans(t("a11y_flights_sr_featured_flight_more_informatio_popup")),
          title: flight.highlightInfo.infoMessage.title
        }
      : undefined;
  /* end - flights_merch_toolkit_offer_pinning_v2 */

  const hasPrefetchedBrandedFareOffers = !!flight.brandedFareOffers && flight.brandedFareOffers?.length > 0;
  const canShowFareSelector =
    boundsVariant !== "outbound" && (flight.requestableBrandedFares || hasPrefetchedBrandedFareOffers);
  const showFareSelectorDefault = canShowFareSelector && displayOptions?.brandedFaresShownByDefault;
  const showCompactFareSelectorButton = !!displayOptions?.brandedFaresShownByDefault || isSplitBounds;

  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [showFareSelector, setShowFareSelector] = useState(showFareSelectorDefault);

  useFetchBrandedFares(
    flight.token,
    isInViewport,
    boundsVariant !== "outbound" && !!flight.requestableBrandedFares,
    true,
    !!flight.brandedFareInfo?.fareName,
    carriers
  );

  const viewDetailsButtonLabel =
    boundsVariant === "outbound" ? t("flights_sr_split_bounds_us_cta_select") : t("flights_apex_sr_flex_cta_main");

  const travellersCount = adults + children.length;

  const flightPriceComponent = rebookProps ? (
    <RebookCostBreakdown
      {...rebookProps}
      breakdownOpen={breakdownOpen}
      setBreakdownOpen={setBreakdownOpen}
      rebookFlow={rebookFlow}
    />
  ) : boundsVariant === "outbound" ? (
    <FlightCardPriceOutbound flight={flight} />
  ) : (
    <FlightCardPrice
      flight={flight}
      breakdownOpen={breakdownOpen}
      setBreakdownOpen={setBreakdownOpen}
      showPricePerPerson={!!boundsVariant || (pricePerPerson && adults + children.length > 1)}
      showAvailableSeats={isOfMetaOrigin() && adults + children.length <= 2}
      totalPriceLabel={i18n.trans(
        travellersCount > 1 ? t("flights_sr_rt_price_per") : t("flights_sr_price_round_trip")
      )}
      popoverCloseAriaLabel={i18n.trans(t("flights_a11y_price_breakdown_close_sr"))}
      boundsVariant={boundsVariant}
    />
  );

  const highlightFireflySR =
    isInternal &&
    flight.token.startsWith("c8e49_") &&
    new URLSearchParams(window.location.search).get("highlight_firefly") === "1";

  const toggleShowFareSelector = useCallback(() => {
    if (showFareSelector) {
      flights_apex_web_ancillaries_micro_conversion_aa.goals.show_fare_options_sr();
      !isSplitBounds && flights_web_flight_card_update_desktop.goals.open_bf_carousel();
    }

    setShowFareSelector(!showFareSelector);
  }, [showFareSelector, isSplitBounds]);

  const toggleFareSelectorIcon = useMemo(
    () => <Icon svg={showFareSelector ? ArrowNavUpIcon : ArrowNavDownIcon} attributes={{ "aria-hidden": true }} />,
    [showFareSelector]
  );

  const viewDetailsButton = (
    <Button
      attributes={{
        "data-testid": "flight_card_bound_select_flight",
        "aria-describedby": `flight-card-${index}`,
        "aria-label": `${i18n.trans(viewDetailsButtonLabel)} ${
          hasFlexibleTicketBadge ? i18n.trans(t("flights_apex_sr_flex_badge")) : ""
        }`
      }}
      text={i18n.trans(viewDetailsButtonLabel)}
      onClick={() => {
        if (showCfarBadge) flights_apex_web_cfar_on_sr_fd_v2.goals.action_click_with_cfar_badge_on_sr();
        if (flight.highlightInfo && isFeaturedOffer)
          flights_merch_toolkit_offer_pinning_v2.trackFeaturedOfferClick(flight.highlightInfo);
        onClick(flight);
      }}
      variant="secondary"
      className={styles.noMarginButton}
    />
  );

  const isResponsiveFixes = !isLarge && !!trackWithDefaultStage("flights_web_cat_responsive_fixes_www", 1);

  return (
    <div id={`flight-card-${index}`} ref={flightCardBoundRef}>
      <Card
        fill
        className={highlightFireflySR ? styles.highlightFirefly : ""}
        variant={shouldPinOfferOnSr ? "elevated" : "neutral"}
      >
        {shouldPinOfferOnSr ? (
          <FlightCardHighlight
            style={FlightCardHighlightStyle.PRIMARY}
            text={pinnedHighlightText}
            showGeniusLogo={isFeaturedOffer}
            infoTooltip={pinnedInfoTooltip}
          />
        ) : shouldShowBestOfferHightlight ? (
          <FlightCardHighlight style={bestOfferHighlightStyle} text={bestOfferHighlightText} textEmphasized={true} />
        ) : null}
        <Frame p={4} direction={isResponsiveFixes ? "column" : "row"}>
          <Frame
            attributes={{
              style: {
                width: isResponsiveFixes ? "100%" : "64%"
              }
            }}
            justifyContent="flex-start"
          >
            {showFlightCardBadges && (
              <FlightCardBadges className={styles.badgesWithBottomMargin}>
                {flight?.badges?.map(({ text, variant, type }, idx) => (
                  <FlightCardBadge
                    key={text + idx}
                    text={text}
                    style={variant}
                    alternative
                    type={type}
                    flightToken={flight.token}
                  />
                ))}
                {isSplitBounds
                  ? null
                  : hasFlexibleTicketBadge &&
                    !showCfarBadge && (
                      <FlightCardBadge
                        style={FlightBadgeVariant.CONSTRUCTIVE}
                        alternative={true}
                        text={i18n.trans(t("flights_apex_sr_flex_badge"))}
                      />
                    )}

                {flight.hasAppleCardBenefits && (
                  <FlightCardBadge
                    style={FlightBadgeVariant.CALLOUT}
                    alternative={true}
                    text={i18n.trans(t("apple_rewards_flights_sr_badge_title"))}
                    flightToken={flight.token}
                  />
                )}
                {showCfarBadge && (
                  <FlightCardBadge
                    ref={cfarBadgeRef}
                    style={FlightBadgeVariant.CONSTRUCTIVE}
                    alternative={true}
                    text={i18n.trans(cfarBadgeCopyTag)}
                    flightToken={flight.token}
                  />
                )}
              </FlightCardBadges>
            )}
            <BaseFareAttributes flight={flight} />
            <CardSegmentsCondensed segments={flight.segments} bigLogo={bigLogo} />

            <CardSplitBoundsLuggageAndBadgesRendererDesktop
              flight={flight}
              boundsVariant={boundsVariant}
              campaignBadges={campaignBadges}
              luggageComponent={
                <FlightCardLuggageDesktop
                  flight={flight}
                  brandedFareInfo={canShowFareSelector ? flight.brandedFareInfo : undefined}
                  brandedFareInfoAlignment="start"
                  buttonAlignment="start"
                  iconsAlignment="start"
                />
              }
              badgesComponent={
                <Stack gap={1} direction="row" alignItems="center">
                  {campaignBadges.map((badge, idx) => (
                    <FlightCardBadge
                      key={`keyCampaignBadge-${idx}`}
                      alternative={false}
                      isInteractive={false}
                      {...badge}
                      flightToken={flight.token}
                    />
                  ))}
                </Stack>
              }
            />
          </Frame>

          <Frame
            direction="column"
            justifyContent={"center"}
            className={mcn(styles.priceBlock, isResponsiveFixes && styles.priceBlockRes)}
          >
            {boundsVariant ? null : (
              <FlightCardLuggageDesktop
                flight={flight}
                brandedFareInfo={canShowFareSelector ? flight.brandedFareInfo : undefined}
              />
            )}

            {campaignBadges.length > 0 ? (
              boundsVariant ? null : (
                <FlightCardBadges
                  className={mcn(
                    styles.badgesInRightSection,
                    !rebookProps?.tripRebookOption
                      ? styles.badgesWithTopMargin
                      : styles.badgesInRightSectionBottomMargin /* The components below and above change padding dynamically. */
                  )}
                >
                  {campaignBadges.map((badge, idx) => (
                    <FlightCardBadge
                      key={`keyCampaignBadge-${idx}`}
                      alternative={false}
                      isInteractive={false}
                      {...badge}
                      flightToken={flight.token}
                    />
                  ))}
                </FlightCardBadges>
              )
            ) : null}

            <div style={{ textAlign: isRTL ? "left" : "right" }}>
              <Frame mt={isSplitBounds ? 0 : 2}>{flightPriceComponent}</Frame>

              {canShowFareSelector && showCompactFareSelectorButton ? (
                <Stack direction="row" grow>
                  <Stack.Item alignSelf="end">
                    <Button
                      attributes={{
                        "aria-expanded": !!showFareSelector,
                        "aria-controls": `flights-fare-selector-${index}`,
                        "aria-describedby": `flight-card-${index}`,
                        "aria-label": showFareSelector
                          ? i18n.trans(t("a11y_flights_apex_bf_sr_hide_options"))
                          : i18n.trans(t("a11y_flights_apex_bf_sr_expand_options"))
                      }}
                      icon={toggleFareSelectorIcon}
                      variant="secondary"
                      onClick={toggleShowFareSelector}
                      className={styles.showFaresButtonWithMargin}
                    />
                  </Stack.Item>
                  <Stack.Item grow>{viewDetailsButton}</Stack.Item>
                </Stack>
              ) : (
                <>
                  {viewDetailsButton}
                  {canShowFareSelector ? (
                    <Button
                      attributes={{
                        "aria-expanded": !!showFareSelector,
                        "aria-controls": `flights-fare-selector-${index}`,

                        "aria-describedby": `flight-card-${index}`
                      }}
                      text={
                        showFareSelector
                          ? i18n.trans(t("flights_apex_sr_flex_cta_hide"))
                          : i18n.trans(t("flights_apex_sr_flex_cta_explore"))
                      }
                      onClick={() => {
                        toggleShowFareSelector();
                      }}
                      variant="tertiary"
                      iconPosition="end"
                      wide={true}
                      icon={
                        <Icon
                          svg={showFareSelector ? ArrowNavUpIcon : ArrowNavDownIcon}
                          attributes={{ "aria-hidden": true }}
                        />
                      }
                      className={styles.showFaresButtonWithMargin}
                    />
                  ) : null}
                </>
              )}
            </div>
          </Frame>
        </Frame>
        {canShowFareSelector && (
          <div id={`flights-fare-selector-${index}`}>{showFareSelector && <FareSelectorDesktop flight={flight} />}</div>
        )}
      </Card>
    </div>
  );
};

const FlightCardDesktop = React.memo(FlightCardBound);
export default FlightCardDesktop;
